
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { ChargingStation } from '@/types/task';
import SearchHeader from '@/components/SearchHeader';
import NearbyStations from '@/components/nearby/NearbyStations';
import StationStats from '@/components/StationStats';
import { Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { subscribeToStationUpdates } from '@/utils/realtime';
import { FilterOptions } from '@/components/StationFilters';
import StationDataProvider from '@/components/station/StationDataProvider';
import StationControls from '@/components/station/StationControls';
import StationContent from '@/components/station/StationContent';

const Index = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [showHomeOnly, setShowHomeOnly] = useState(false);
  const [filters, setFilters] = useState<FilterOptions | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const applyFilters = (
    stationsToFilter: ChargingStation[], 
    query: string, 
    filterOptions: FilterOptions | null,
    homeOnly: boolean,
    setFilteredStations: (stations: ChargingStation[]) => void
  ) => {
    let result = [...stationsToFilter];

    if (query) {
      const queryLower = query.toLowerCase();
      result = result.filter(station => 
        station.title?.toLowerCase().includes(queryLower) ||
        station.location?.toLowerCase().includes(queryLower) ||
        station.address?.toLowerCase().includes(queryLower) ||
        station.chargerType?.toLowerCase().includes(queryLower) ||
        station.amenities?.some(amenity => amenity.toLowerCase().includes(queryLower))
      );
    }

    if (homeOnly) {
      result = result.filter(station => station.isHomeCharger);
    }

    if (filterOptions) {
      result = result.filter(station => {
        if (station.price < filterOptions.price[0] || station.price > filterOptions.price[1]) {
          return false;
        }
        if (station.powerOutput < filterOptions.powerOutput[0] || 
            station.powerOutput > filterOptions.powerOutput[1]) {
          return false;
        }
        if (!filterOptions.chargerTypes.includes(station.chargerType)) {
          return false;
        }
        if (filterOptions.availableOnly && !station.available) {
          return false;
        }
        if (filterOptions.homeOnly && !station.isHomeCharger) {
          return false;
        }
        return true;
      });
    }

    setFilteredStations(result);
  };

  const handleStationSelect = (station: ChargingStation) => {
    navigate(`/station/${station.id}`);
  };

  return (
    <div className="container max-w-7xl py-6 px-4 md:px-6">
      <div className="mb-8">
        <SearchHeader onSearch={setSearchQuery} />
        <div className="mt-4 text-sm text-muted-foreground">
          Discover EV charging stations in your area
        </div>
      </div>

      <StationDataProvider>
        {({ 
          stations, 
          filteredStations, 
          isLoading, 
          isRefreshing, 
          handleRefresh,
          setFilteredStations 
        }) => {
          useEffect(() => {
            applyFilters(stations, searchQuery, filters, showHomeOnly, setFilteredStations);
          }, [searchQuery, filters, showHomeOnly, stations]);

          useEffect(() => {
            const unsubscribe = subscribeToStationUpdates((updatedStation) => {
              setFilteredStations(currentStations => 
                currentStations.map(station => 
                  station.id === updatedStation.id ? updatedStation : station
                )
              );
            });

            return () => {
              unsubscribe();
            };
          }, [toast]);

          return (
            <>
              <StationStats stations={stations} />
              
              <StationControls 
                showMap={showMap}
                showHomeOnly={showHomeOnly}
                isRefreshing={isRefreshing}
                onToggleMap={() => setShowMap(!showMap)}
                onToggleHomeOnly={() => setShowHomeOnly(!showHomeOnly)}
                onRefresh={handleRefresh}
                onFilterChange={(newFilters) => {
                  setFilters(newFilters);
                  if (newFilters.homeOnly !== showHomeOnly) {
                    setShowHomeOnly(newFilters.homeOnly);
                  }
                }}
              />

              <div className="w-full md:w-auto mb-6">
                <NearbyStations 
                  stations={filteredStations} 
                  onStationSelect={handleStationSelect}
                />
              </div>
              
              <h1 className="text-2xl font-bold mb-6 flex items-center">
                <Zap className="h-6 w-6 mr-2 text-blue-500" />
                EV Charging Stations
              </h1>
              
              <StationContent 
                showMap={showMap}
                filteredStations={filteredStations}
                searchQuery={searchQuery}
                isLoading={isLoading}
                onStationSelect={handleStationSelect}
                onClearFilters={() => {
                  setSearchQuery('');
                  setFilters(null);
                  setShowHomeOnly(false);
                }}
              />
            </>
          );
        }}
      </StationDataProvider>
    </div>
  );
};

export default Index;
